import { type TCompanyResponse } from "@/api/companies";
import {
  TOnboardingAnswerPayload,
  TOnboardingCompletionMessage,
  TOnboardingHistoryEntry,
  TOnboardingMessage,
  TOnboardingMetaPublic,
  TOnboardingProgress,
  TOnboardingQuestion,
  onboardingService,
} from "@/api/onboarding";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useStore } from "@/store";
import {
  AWAIT_STALL_MS,
  MAX_SILENT_RECONNECTS,
  SILENT_RECONNECT_MS,
  classifyClose,
  type TCloseOutcome,
} from "@/lib/onboarding/sessionAdapter";
import { sanitizeUpstreamErrorMessage } from "@/utils/sanitizeUpstreamErrorMessage";
import { tokenStorage } from "@/utils/tokenStorage";
import { handleWebSocketAuthError, handleWebSocketCloseAuthError } from "@/utils/websocketUtils";

type TOnboardingSessionStatus =
  | "idle"
  | "initializing"
  | "connecting"
  | "connected"
  | "error"
  | "completed"
  | "processing_summary";

type TOnboardingSessionError = {
  message: string;
  field?: string;
};

export type TOnboardingSessionHistoryItem = {
  question: TOnboardingQuestion;
  answer: {
    text?: string | null;
    fileId?: string | null;
    fileName?: string | null;
  };
};

export type TOnboardingCompletionResult = {
  companyId: string;
  history: TOnboardingSessionHistoryItem[];
  progress: TOnboardingProgress;
  message: string;
  nextSteps: string[];
  aiSummary?: string | null;
};

type TPendingAnswer = {
  question: TOnboardingQuestion;
  answer: TOnboardingSessionHistoryItem["answer"];
  payload: TOnboardingAnswerPayload;
};

type TOnboardingCompletionState = {
  message: string;
  progress: TOnboardingProgress;
  nextSteps: string[];
  aiSummary?: string | null;
};

type TUseOnboardingSessionOptions = {
  autoConnect?: boolean;
  onCompletion?: (payload: TOnboardingCompletionResult) => void;
};

type TOnboardingSessionState = {
  status: TOnboardingSessionStatus;
  company: TCompanyResponse | null;
  currentQuestion: TOnboardingQuestion | null;
  progress: TOnboardingProgress | null;
  history: TOnboardingSessionHistoryItem[];
  message?: string;
  ackMessage?: string;
  error?: TOnboardingSessionError;
  validationError?: TOnboardingSessionError;
  completion?: TOnboardingCompletionState;
  isAwaitingNextStep: boolean;
  /** The AI's closing turn, rendered as a chat bubble while the summary generates. */
  finalMessage?: { id: string; text: string };
  onboardingMeta?: TOnboardingMetaPublic;
  /** A sent answer the backend failed to process — the UI offers Resend instead of retyping. */
  failedSend?: { text?: string | null; fileName?: string | null };
  /** How the socket ended when it closed abnormally — lets the UI route, not just error. */
  closeOutcome?: TCloseOutcome;
};

type TLegacyHistoryEntry = {
  turn?: number;
  question_text?: string;
  answer_text?: string | null;
  has_file?: boolean;
};

const normaliseEntry = (entry: unknown): TOnboardingHistoryEntry => {
  // New format: { question: {...}, answer: {...} }
  if (entry !== null && typeof entry === "object" && "question" in entry) {
    const typed = entry as Record<string, unknown>;
    if (typed.question !== null && typeof typed.question === "object") {
      return entry as TOnboardingHistoryEntry;
    }
  }
  // Legacy flat format: { question_text, answer_text, turn, has_file }
  const flat = (entry ?? {}) as TLegacyHistoryEntry;
  return {
    question: {
      id: String(flat.turn ?? Math.random()),
      question_text: flat.question_text ?? "",
      question_description: undefined,
      data_type: "text",
      required: true,
      order: flat.turn ?? 0,
      choices: undefined,
      metadata: {},
      answer_requirements: {
        require_text: true,
        require_file: false,
        accept_text: true,
        accept_file: false,
      },
    },
    answer: {
      value: flat.answer_text ?? null,
      has_file: flat.has_file ?? false,
      has_text: Boolean(flat.answer_text),
      file_id: undefined,
      file_name: undefined,
    },
  };
};

const mapHistoryEntries = (
  entries: TOnboardingHistoryEntry[] | undefined,
  enrichQuestion: (question: TOnboardingQuestion | null) => TOnboardingQuestion | null
): TOnboardingSessionHistoryItem[] => {
  if (!entries || entries.length === 0) {
    return [];
  }

  return entries.map((raw) => {
    const entry = normaliseEntry(raw);
    return {
      question:
        enrichQuestion({
          id: entry.question.id,
          question_text: entry.question.question_text,
          question_description: entry.question.question_description,
          data_type: entry.question.data_type,
          required: entry.question.required,
          order: entry.question.order,
          choices: entry.question.choices,
          metadata: entry.question.metadata,
          answer_requirements: entry.question.answer_requirements,
        }) ?? entry.question,
      answer: {
        text: typeof entry.answer.value === "number" ? String(entry.answer.value) : (entry.answer.value ?? undefined),
        fileId: entry.answer.file_id ?? null,
        fileName: entry.answer.file_name ?? null,
      },
    };
  });
};

const initialState: TOnboardingSessionState = {
  status: "idle",
  company: null,
  currentQuestion: null,
  progress: null,
  history: [],
  message: undefined,
  ackMessage: undefined,
  error: undefined,
  validationError: undefined,
  completion: undefined,
  isAwaitingNextStep: false,
  finalMessage: undefined,
  onboardingMeta: undefined,
};

export const useOnboardingSession = ({ autoConnect = true, onCompletion }: TUseOnboardingSessionOptions = {}) => {
  const [state, setState] = useState<TOnboardingSessionState>(initialState);
  const websocketRef = useRef<WebSocket | null>(null);
  const pendingAnswerRef = useRef<TPendingAnswer | null>(null);
  const completionNotifiedRef = useRef(false);
  const reconnectAbortRef = useRef<boolean>(false);
  // Guards the async window inside `connect` (before the socket is assigned to
  // websocketRef) so two rapid calls can't each open a socket and double the welcome.
  const connectingRef = useRef<boolean>(false);
  const completedRef = useRef(false);
  const authHeadersRef = useRef<HeadersInit | undefined>(undefined);
  // Synchronous double-send guard — React state can't propagate fast enough to
  // stop a double-click from firing two answer frames; the second would be
  // interpreted by the backend as the answer to the NEXT question.
  const sendingRef = useRef<boolean>(false);
  // Bounded silent-reconnect counter — one automatic retry, then manual.
  const silentReconnectsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard so a replayed question only triggers one automatic resubmit.
  const resentOnReplayRef = useRef<string | null>(null);
  // Arms while an answer is awaiting the next step — a stall converts to a
  // bounded reconnect rather than an infinite "thinking" state.
  const awaitWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enrichQuestion = useCallback(
    (question: TOnboardingQuestion | null): TOnboardingQuestion | null => question,
    []
  );

  const clearAwaitWatchdog = useCallback(() => {
    if (awaitWatchdogRef.current) {
      clearTimeout(awaitWatchdogRef.current);
      awaitWatchdogRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
    completionNotifiedRef.current = false;
    pendingAnswerRef.current = null;
    completedRef.current = false;
    sendingRef.current = false;
    silentReconnectsRef.current = 0;
    resentOnReplayRef.current = null;
    clearAwaitWatchdog();
    clearReconnectTimer();
  }, [clearAwaitWatchdog, clearReconnectTimer]);

  const closeWebsocket = useCallback((code?: number, reason?: string) => {
    if (websocketRef.current) {
      try {
        websocketRef.current.close(code, reason);
      } catch (error) {
        console.error("[onboarding] Failed to close WebSocket", { error, code, reason });
      } finally {
        websocketRef.current = null;
      }
    }
  }, []);

  const connect = useCallback(async () => {
    if (reconnectAbortRef.current) {
      return;
    }

    // Prevent duplicate connections if one is already open or connecting
    if (
      websocketRef.current &&
      websocketRef.current.readyState !== WebSocket.CLOSED
    ) {
      return;
    }

    // `connect` awaits fetchCompanies before creating the socket, so the ref check
    // above is not enough: two concurrent calls (React StrictMode double-mount, or the
    // autoConnect + backup-reconnect effects) could both pass it during the await and
    // open two sockets — the backend then sends the welcome on each, producing the
    // duplicate greeting. This synchronous flag closes that window.
    if (connectingRef.current) {
      return;
    }
    connectingRef.current = true;

    setState((prev) => ({
      ...prev,
      status: "initializing",
      error: undefined,
      validationError: undefined,
      message: undefined,
    }));

    completedRef.current = false;

    const token = tokenStorage.getAccessToken();

    if (!token) {
      setState((prev) => ({
        ...prev,
        status: "error",
        error: {
          message: "Authentication token missing. Please log in again.",
        },
      }));
      connectingRef.current = false;
      return;
    }

    const authHeaders: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };
    authHeadersRef.current = authHeaders;

    // Get companies from store and find incomplete company
    const getCompanies = useStore.getState().getCompanies;
    const fetchCompanies = useStore.getState().fetchCompanies;

    // Fetch companies if needed
    await fetchCompanies({ force: false });
    const companies = getCompanies();

    if (!companies || companies.length === 0) {
      setState((prev) => ({
        ...prev,
        status: "error",
        error: {
          message: "No companies found. Please create a company first.",
        },
      }));
      connectingRef.current = false;
      return;
    }

    // Find incomplete company (for onboarding)
    const company = companies.find((c) => !c.onboarding_completed);

    if (!company) {
      setState((prev) => ({
        ...prev,
        status: "error",
        error: {
          message: "No company available for onboarding. Please create a company to continue.",
        },
      }));
      connectingRef.current = false;
      return;
    }

    setState((prev) => ({
      ...prev,
      company,
      status: "connecting",
    }));

    const websocketURL = onboardingService.getWebSocketURL(company.id, token);

    const websocket = new WebSocket(websocketURL);

    websocketRef.current = websocket;
    reconnectAbortRef.current = false;
    // Socket is now assigned; the ref check at the top of `connect` guards further calls.
    connectingRef.current = false;

    websocket.onopen = () => {
      setState((prev) => ({
        ...prev,
        status: "connected",
        message: undefined,
        error: undefined,
      }));
    };

    websocket.onerror = (event) => {
      console.error("[onboarding] WebSocket error", { event });
      setState((prev) => ({
        ...prev,
        status: "error",
        error: {
          message: "WebSocket error occurred. Please try again.",
        },
      }));
    };

    websocket.onclose = (event) => {
      websocketRef.current = null;
      sendingRef.current = false;
      clearAwaitWatchdog();

      // Check for authentication failure (code 1008 with "Authentication failed" reason)
      // This must be checked first before any other close handling
      if (handleWebSocketCloseAuthError(event.code, event.reason)) {
        return; // Logout is handled
      }

      if (reconnectAbortRef.current) {
        return;
      }

      if (completedRef.current) {
        setState((prev) => ({
          ...prev,
          status: "completed",
        }));
        return;
      }

      const outcome = classifyClose(event.code, event.reason);

      // A finished company's socket closes with an "already completed" reason.
      // That is the completion path — drive it instead of showing an error.
      if (outcome === "completed") {
        setState((prev) => ({
          ...prev,
          status: "processing_summary",
          currentQuestion: null,
          isAwaitingNextStep: false,
        }));
        return;
      }

      // Unexpected drop while work may still be in flight — try one bounded
      // silent reconnect before surfacing a manual-reconnect error.
      if (event.code !== 1000 && silentReconnectsRef.current < MAX_SILENT_RECONNECTS) {
        silentReconnectsRef.current += 1;
        setState((prev) => ({
          ...prev,
          status: "connecting",
          closeOutcome: outcome,
        }));
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          void connect();
        }, SILENT_RECONNECT_MS);
        return;
      }

      setState((prev) => {
        if (prev.status === "processing_summary" || prev.status === "completed") {
          return prev;
        }
        const friendly =
          outcome === "vetting_required"
            ? "Your investor access is pending review. You'll be able to continue once approved."
            : outcome === "access_denied"
              ? "You don't have access to continue this onboarding."
              : (event.reason ?? "Connection lost. Please try again.");
        return {
          ...prev,
          status: "error",
          closeOutcome: outcome,
          error: { message: friendly },
        };
      });
    };

    websocket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data as string) as TOnboardingMessage;
        // Any real frame means the turn resolved — disarm the stall watchdog.
        // (pong is excluded: heartbeats shouldn't mask a stalled turn.)
        if (parsed.type !== "pong") clearAwaitWatchdog();
        switch (parsed.type) {
          case "question":
          case "resume": {
            const questionPayload = parsed;
            const historyEntries = mapHistoryEntries(questionPayload.history, enrichQuestion);
            const pending = pendingAnswerRef.current;

            // Replay suppression: the backend re-sent the exact question we just
            // answered without acking it — the answer never landed server-side.
            // Resubmit the same payload once instead of re-asking the user.
            if (
              pending &&
              questionPayload.question?.id === pending.question.id &&
              resentOnReplayRef.current !== pending.question.id
            ) {
              resentOnReplayRef.current = pending.question.id;
              websocket.send(JSON.stringify(pending.payload));
              break;
            }

            // A *different* question arriving while a send was pending means the
            // answer did land — fold the pending bubble into history so the
            // transcript doesn't lose it.
            const pendingHistory =
              pending && questionPayload.question?.id !== pending.question.id
                ? [{ question: pending.question, answer: pending.answer }]
                : [];
            pendingAnswerRef.current = null;
            sendingRef.current = false;
            resentOnReplayRef.current = null;

            setState((prev) => ({
              ...prev,
              status: "connected",
              currentQuestion: enrichQuestion(questionPayload.question),
              progress: questionPayload.progress,
              message: questionPayload.message,
              history:
                parsed.type === "resume" && historyEntries.length > 0
                  ? [...historyEntries, ...pendingHistory]
                  : pendingHistory.length > 0
                    ? [...prev.history, ...pendingHistory]
                    : prev.history,
              onboardingMeta: questionPayload.onboarding ?? prev.onboardingMeta,
              validationError: undefined,
              ackMessage: undefined,
              failedSend: undefined,
              isAwaitingNextStep: false,
            }));
            break;
          }
          case "answer_received": {
            const pendingAnswer = pendingAnswerRef.current;
            setState((prev) => {
              const history = pendingAnswer ? [...prev.history, pendingAnswer] : prev.history;

              return {
                ...prev,
                ackMessage: parsed.message,
                history,
                validationError: undefined,
                // Keep isAwaitingNextStep true — the thinking bubble stays until the
                // next "question" event arrives and clears it there.
              };
            });
            pendingAnswerRef.current = null;
            sendingRef.current = false;
            break;
          }
          case "validation_error": {
            const pending = pendingAnswerRef.current;
            pendingAnswerRef.current = null;
            sendingRef.current = false;
            setState((prev) => ({
              ...prev,
              validationError: {
                message: parsed.message,
                field: parsed.field,
              },
              failedSend: pending
                ? { text: pending.answer.text, fileName: pending.answer.fileName }
                : prev.failedSend,
              isAwaitingNextStep: false,
            }));
            break;
          }
          case "processing": {
            const processingPayload = parsed;

            setState((prev) => ({
              ...prev,
              status: "processing_summary",
              message: processingPayload.message,
              progress: processingPayload.progress,
              // The AI's closing turn. Older backends omit `final_message` and carry the
              // same text in `message`, so fall back to that rather than losing it.
              finalMessage: processingPayload.final_message
                ? { id: processingPayload.final_message.id, text: processingPayload.final_message.text }
                : processingPayload.message
                  ? { id: "ai-final-message", text: processingPayload.message }
                  : undefined,
              onboardingMeta: processingPayload.onboarding ?? prev.onboardingMeta,
              currentQuestion: null,
              isAwaitingNextStep: false,
            }));

            // Close WebSocket as we'll use polling instead
            closeWebsocket(1000, "Processing summary");
            break;
          }
          // unreachable: no backend path emits this frame (only the schema mentions it)
          case "completion": {
            const completionPayload = parsed as TOnboardingCompletionMessage & { ai_summary?: string | null };

            setState((prev) => ({
              ...prev,
              status: "completed",
              completion: {
                message: completionPayload.message,
                progress: completionPayload.progress,
                nextSteps: completionPayload.next_steps ?? [],
                aiSummary: completionPayload.ai_summary ?? null,
              },
              message: completionPayload.message,
              currentQuestion: null,
              isAwaitingNextStep: false,
            }));

            completedRef.current = true;
            closeWebsocket(1000, "Onboarding complete");
            break;
          }
          case "progress": {
            setState((prev) => ({
              ...prev,
              progress: parsed.progress,
            }));
            break;
          }
          case "error":
          case "info": {
            // Check for authentication failure
            if (parsed.type === "error" && handleWebSocketAuthError(parsed.message, parsed.type)) {
              return; // Logout is handled
            }

            // A completed company replays this info frame before the socket
            // closes — drive the normal completion path, not an error screen.
            if (parsed.type === "info" && classifyClose(0, parsed.message) === "completed") {
              setState((prev) => ({
                ...prev,
                status: "processing_summary",
                currentQuestion: null,
                failedSend: undefined,
                isAwaitingNextStep: false,
              }));
              break;
            }

            const displayMessage =
              parsed.type === "error"
                ? sanitizeUpstreamErrorMessage(
                    parsed.message,
                    "AI service temporarily unavailable. Please try again later."
                  )
                : parsed.message;

            // Keep the pending answer so the UI can offer Resend without retyping.
            const pending = parsed.type === "error" ? pendingAnswerRef.current : null;
            if (parsed.type === "error") {
              sendingRef.current = false;
            }

            setState((prev) => ({
              ...prev,
              message: displayMessage,
              error: parsed.type === "error" ? { message: displayMessage } : prev.error,
              failedSend: pending
                ? { text: pending.answer.text, fileName: pending.answer.fileName }
                : prev.failedSend,
              // A terminal frame must release the thinking state — otherwise the
              // user is left watching an indicator for a turn that already failed.
              isAwaitingNextStep: parsed.type === "error" ? false : prev.isAwaitingNextStep,
            }));
            break;
          }
          case "pong":
          default: {
            break;
          }
        }
      } catch (error) {
        console.error("[onboarding] Failed to parse WebSocket message", { error });
      }
    };
  }, [closeWebsocket, clearAwaitWatchdog, enrichQuestion]);

  const disconnect = useCallback(() => {
    reconnectAbortRef.current = true;
    closeWebsocket(1000, "Client disconnected");
    resetState();
  }, [closeWebsocket, resetState]);

  useEffect(() => {
    if (!autoConnect) {
      return;
    }

    reconnectAbortRef.current = false;
    void connect();

    return () => {
      reconnectAbortRef.current = true;
      closeWebsocket(1000, "Component unmounted");
    };
  }, [autoConnect, connect, closeWebsocket]);

  useEffect(() => {
    if (
      state.status === "completed" &&
      state.company &&
      state.completion &&
      !completionNotifiedRef.current &&
      typeof onCompletion === "function"
    ) {
      completionNotifiedRef.current = true;
      const completionPayload: TOnboardingCompletionResult = {
        companyId: state.company.id,
        history: state.history,
        progress: state.completion.progress,
        message: state.completion.message,
        nextSteps: state.completion.nextSteps,
        aiSummary: state.completion.aiSummary,
      };
      onCompletion(completionPayload);
    }
  }, [onCompletion, state]);

  const submitAnswer = useCallback(
    (payload: {
      question: TOnboardingQuestion;
      text?: string | null;
      fileId?: string | null;
      fileName?: string | null;
    }) => {
      if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        setState((prev) => ({
          ...prev,
          error: {
            message: "Connection is not ready. Please try again.",
          },
        }));
        return;
      }

      // One in-flight send only — a double-click must not fire a second answer
      // frame that the backend would read as the answer to the next question.
      if (sendingRef.current) {
        return;
      }
      sendingRef.current = true;

      const answerPayload: TOnboardingAnswerPayload = {
        type: "answer",
        question_id: payload.question.id,
        answer_text: payload.text ?? null,
        file_id: payload.fileId ?? null,
        data_type: payload.question.data_type,
      };

      pendingAnswerRef.current = {
        question: payload.question,
        answer: {
          text: payload.text ?? null,
          fileId: payload.fileId ?? null,
          fileName: payload.fileName ?? null,
        },
        payload: answerPayload,
      };

      websocketRef.current.send(JSON.stringify(answerPayload));
      setState((prev) => ({
        ...prev,
        validationError: undefined,
        ackMessage: undefined,
        failedSend: undefined,
        isAwaitingNextStep: true,
      }));

      // Stall watchdog: if no next-step frame lands within the bound, drop and
      // reconnect once — the "thinking forever" screen becomes a recovery path.
      clearAwaitWatchdog();
      awaitWatchdogRef.current = setTimeout(() => {
        awaitWatchdogRef.current = null;
        if (websocketRef.current) {
          try {
            websocketRef.current.close(4000, "await timeout");
          } catch {
            /* socket may already be gone */
          }
        }
      }, AWAIT_STALL_MS);
    },
    [clearAwaitWatchdog]
  );

  /**
   * Resend the last answer after a processing failure — same payload, no retype.
   * The backend treats it as the answer to the checkpoint's last question, which
   * is still the one that failed.
   */
  const retryPendingAnswer = useCallback(() => {
    const pending = pendingAnswerRef.current;
    if (!pending || sendingRef.current) return;
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      // Dead socket — reconnect; on replay of the same question the pending
      // answer auto-resubmits via the replay-suppression path.
      void connect();
      return;
    }
    sendingRef.current = true;
    pendingAnswerRef.current = pending;
    websocketRef.current.send(JSON.stringify(pending.payload));
    setState((prev) => ({
      ...prev,
      failedSend: undefined,
      error: undefined,
      isAwaitingNextStep: true,
    }));
    clearAwaitWatchdog();
    awaitWatchdogRef.current = setTimeout(() => {
      awaitWatchdogRef.current = null;
      if (websocketRef.current) {
        try {
          websocketRef.current.close(4000, "await timeout");
        } catch {
          /* socket may already be gone */
        }
      }
    }, AWAIT_STALL_MS);
  }, [clearAwaitWatchdog, connect]);

  const uploadAnswerFile = useCallback(async (file: File, context: { companyId: string; questionId: string }) => {
    const headers = authHeadersRef.current;
    const { data, error } = await onboardingService.uploadAnswerFile(
      {
        companyId: context.companyId,
        questionId: context.questionId,
        file,
      },
      headers ? { headers } : undefined
    );

    if (error || !data?.data?.id) {
      setState((prev) => ({
        ...prev,
        error: {
          message: error?.message ?? "Failed to upload file. Please try again.",
        },
      }));
      return null;
    }

    return data.data.id;
  }, []);

  const currentProgress = useMemo(() => {
    if (!state.progress) {
      return null;
    }

    const { completion_percentage: percentage } = state.progress;

    return {
      ...state.progress,
      percentage,
    };
  }, [state.progress]);

  return {
    status: state.status,
    company: state.company,
    currentQuestion: state.currentQuestion,
    progress: currentProgress,
    history: state.history,
    message: state.message,
    ackMessage: state.ackMessage,
    error: state.error,
    validationError: state.validationError,
    completion: state.completion,
    isAwaitingNextStep: state.isAwaitingNextStep,
    finalMessage: state.finalMessage,
    onboardingMeta: state.onboardingMeta,
    failedSend: state.failedSend,
    closeOutcome: state.closeOutcome,
    submitAnswer,
    retryPendingAnswer,
    uploadAnswerFile,
    reconnect: connect,
    disconnect,
  };
};
